import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";
import { formatDate } from "utils/dateFormatter";

const CONTRACT_STATE_COLORS = {
  active: "success",
  pending: "secondary",
  suspended: "error",
  terminated: "dark",
  expired: "warning",
};

const fieldLabelSx = {
  display: "flex",
  flexDirection: "column",
  gap: 0.25,
  alignItems: "flex-start",
};

function ContractField({ label, children }) {
  return (
    <MDBox sx={fieldLabelSx}>
      <MDTypography variant="caption" fontWeight="medium" color="secondary">
        {label}
      </MDTypography>
      {children}
    </MDBox>
  );
}

ContractField.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

function formatStatusLabel(status) {
  if (!status) return "Unknown";
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function CustomerContractsMobileCards({
  contracts,
  loading,
  terminationScheduledByContractId,
  onViewContract,
  emptyMessage,
}) {
  if (loading) {
    return (
      <MDBox display="flex" justifyContent="center" py={3}>
        <CircularProgress size={28} />
      </MDBox>
    );
  }

  if (!contracts?.length) {
    return (
      <MDBox display="flex" justifyContent="center" py={3}>
        <MDTypography variant="body2" color="text">
          {emptyMessage}
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox display="flex" flexDirection="column" gap={2}>
      {contracts.map((contract, index) => {
        const contractId = Number(contract.contract_id || 0);
        const hasTerminationScheduled = Boolean(terminationScheduledByContractId[contractId]);

        return (
          <Card
            key={contract.contract_id || index}
            variant="outlined"
            sx={{ overflow: "hidden" }}
          >
            <MDBox p={2}>
              <MDBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                <MDBox minWidth={0} flex={1}>
                  <MDTypography variant="caption" color="secondary" fontWeight="medium">
                    Contract Type
                  </MDTypography>
                  <MDTypography variant="button" fontWeight="medium" sx={{ wordBreak: "break-word" }}>
                    {contract.contract_type || "N/A"}
                  </MDTypography>
                </MDBox>
                <MDButton
                  variant="text"
                  color="info"
                  size="small"
                  onClick={() => onViewContract(contract)}
                  sx={{ minWidth: "auto", flexShrink: 0 }}
                >
                  View
                </MDButton>
              </MDBox>

              <Divider sx={{ my: 1.5 }} />

              <MDBox display="flex" flexDirection="column" gap={1.25}>
                <ContractField label="Service Plan">
                  <MDTypography variant="body2" color="text" sx={{ wordBreak: "break-word" }}>
                    {contract.service_plan || "N/A"}
                  </MDTypography>
                </ContractField>

                <ContractField label="Status">
                  <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <MDBadge
                      badgeContent={formatStatusLabel(contract.status)}
                      color={CONTRACT_STATE_COLORS[contract.status] || "light"}
                      variant="gradient"
                      size="sm"
                    />
                    {hasTerminationScheduled ? (
                      <MDBadge
                        badgeContent={
                          <MDBox
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              lineHeight: 1.05,
                            }}
                          >
                            <span>Termination</span>
                            <span>Scheduled</span>
                          </MDBox>
                        }
                        color="warning"
                        variant="gradient"
                        size="sm"
                      />
                    ) : null}
                  </MDBox>
                </ContractField>

                <ContractField label="Dates (Start / End)">
                  <MDBox sx={{ display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
                    <MDTypography variant="body2" color="text">
                      {formatDate(contract.start_date) || "N/A"}
                    </MDTypography>
                    <MDTypography variant="body2" color="text">
                      {formatDate(contract.end_date) || "N/A"}
                    </MDTypography>
                  </MDBox>
                </ContractField>

                <ContractField label="Monthly Fee">
                  <MDTypography variant="body2" color="text" fontWeight="medium">
                    R {Number(contract.monthly_fee || 0).toFixed(2)}
                  </MDTypography>
                </ContractField>

                <ContractField label="Signed Date">
                  <MDTypography variant="body2" color="text">
                    {formatDate(contract.signed_date) || "Not signed"}
                  </MDTypography>
                </ContractField>
              </MDBox>
            </MDBox>
          </Card>
        );
      })}
    </MDBox>
  );
}

CustomerContractsMobileCards.propTypes = {
  contracts: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  terminationScheduledByContractId: PropTypes.objectOf(PropTypes.bool),
  onViewContract: PropTypes.func.isRequired,
  emptyMessage: PropTypes.string,
};

CustomerContractsMobileCards.defaultProps = {
  loading: false,
  terminationScheduledByContractId: {},
  emptyMessage: "No contracts found",
};

export default CustomerContractsMobileCards;
